/**
 * Página dedicada de Bairros com auto-complete geográfico:
 * 1. Usuário escolhe Estado (IBGE)
 * 2. Usuário escolhe Cidade (IBGE)
 * 3. App consulta bairros oficiais via Overpass API (OpenStreetMap)
 * 4. Se não encontrar, permite digitar manualmente
 *
 * Reusa o CrudPage por baixo para toda a UX de tabela + import/export + bulk delete,
 * mas substitui o formulário "Novo" por um form com selects Estado/Cidade/Bairro.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload, Download, Search, MapPin, Loader2, Compass } from "lucide-react";
import { PasswordConfirm } from "@/components/PasswordConfirm";
import { useAuth } from "@/context/AuthContext";
import { fetchUFs, fetchCidades, fetchBairros } from "@/lib/geo";
import { hasPerm } from "@/lib/roles";
import { CidadeCard } from "./bairros/CidadeCard";
import { DescobertaDialog } from "./bairros/DescobertaDialog";
import { SugerirRegioesDialog } from "./bairros/SugerirRegioesDialog";
import { RegiaoSugestaoHint } from "./bairros/RegiaoSugestaoHint";
import { useRegiaoSugestao } from "./bairros/useRegiaoSugestao";

export default function Bairros() {
  const { currentEmpresaId, user } = useAuth();
  const [items, setItems] = useState([]);
  const [descJob, setDescJob] = useState(null);
  const [sugerirOpen, setSugerirOpen] = useState(false);
  const canDescobrir = hasPerm(user, "bairros", "i") || hasPerm(user, "bairros", "a");
  const canAlterar = hasPerm(user, "bairros", "a");
  const [regioes, setRegioes] = useState([]);
  const [ufs, setUfs] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [bairrosGeo, setBairrosGeo] = useState([]);
  const [uf, setUf] = useState("");
  const [cidade, setCidade] = useState("");
  const [bairro, setBairro] = useState("");
  const [regiaoId, setRegiaoId] = useState("");
  const [regiaoManual, setRegiaoManual] = useState(false);
  const [loadingGeo, setLoadingGeo] = useState(false);
  const [geoSource, setGeoSource] = useState(""); // "cache" | "overpass" | "error" | ""
  const [selected, setSelected] = useState(new Set());
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [pwdConfirm, setPwdConfirm] = useState(null);
  const [search, setSearch] = useState("");
  const fileRef = useRef(null);

  // Sugestão automática de Região pela posição do bairro (até 2 km do centro = Centro; depois pela direção).
  const sugestao = useRegiaoSugestao(bairro, { cidade, uf, enabled: canDescobrir });
  const sugeridaId = sugestao.status === "ok" ? sugestao.data?.sugestao?.regiao_id : null;
  useEffect(() => { if (sugeridaId && !regiaoManual) setRegiaoId(sugeridaId); }, [sugeridaId, regiaoManual]);
  const onBairroChange = (v) => { setBairro(v); setRegiaoManual(false); };
  const onRegiaoChange = (v) => { setRegiaoId(v); setRegiaoManual(true); };
  const semRegiaoCount = useMemo(() => items.filter((it) => !it.regiao_id).length, [items]);

  const toggleSelected = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelected((s) => s.size === filteredItems.length && filteredItems.length > 0 ? new Set() : new Set(filteredItems.map((it) => it.id)));
  const clearSelection = () => setSelected(new Set());

  const load = async () => {
    const [b, r] = await Promise.all([api.get("/bairros"), api.get("/regioes")]);
    setItems(b.data); setRegioes(r.data);
  };
  useEffect(() => {
    load();
    fetchUFs().then(setUfs).catch(() => setUfs([]));
  }, [currentEmpresaId]);

  useEffect(() => {
    if (!uf) { setCidades([]); setCidade(""); setBairrosGeo([]); return; }
    fetchCidades(uf).then(setCidades).catch(() => setCidades([]));
  }, [uf]);

  useEffect(() => {
    if (!uf || !cidade) { setBairrosGeo([]); setGeoSource(""); return; }
    setLoadingGeo(true); setGeoSource("");
    fetchBairros(uf, cidade)
      .then((r) => { setBairrosGeo(r.bairros); setGeoSource(r.source); })
      .catch(() => { setBairrosGeo([]); setGeoSource("error"); })
      .finally(() => setLoadingGeo(false));
  }, [uf, cidade]);

  const filteredItems = useMemo(() => {
    const q = search.toLowerCase().trim();
    return q ? items.filter((it) => (it.nome || "").toLowerCase().includes(q)) : items;
  }, [items, search]);

  const submit = async () => {
    const nomeFinal = (bairro || "").trim();
    if (!nomeFinal) return toast.error("Digite ou selecione um bairro");
    if (!regiaoId) return toast.error("Selecione a Região do bairro");
    try {
      await api.post("/bairros", { nome: nomeFinal, regiao_id: regiaoId });
      toast.success(`"${nomeFinal}" adicionado`);
      setBairro(""); setRegiaoId(""); setRegiaoManual(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const del = async (id) => {
    try { await api.delete(`/bairros/${id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir"); }
  };
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      const r = await api.post("/bairros/bulk-delete", { ids });
      toast.success(`${r.data.deleted} bairro(s) excluído(s)`);
      clearSelection(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const openEdit = (it) => { setEditItem(it); setEditForm({ ...it }); };
  // Ao editar um bairro ainda sem região, sugere a região pela posição.
  const sugestaoEdit = useRegiaoSugestao(editItem ? editForm.nome : "", { enabled: !!editItem && !editItem.regiao_id && canDescobrir });
  const sugeridaEditId = sugestaoEdit.status === "ok" ? sugestaoEdit.data?.sugestao?.regiao_id : null;
  useEffect(() => { if (sugeridaEditId) setEditForm((f) => (f.regiao_id ? f : { ...f, regiao_id: sugeridaEditId })); }, [sugeridaEditId]);
  const saveEdit = async () => {
    if (!editForm.regiao_id) return toast.error("Selecione a Região do bairro");
    try { await api.put(`/bairros/${editItem.id}`, editForm); toast.success("Atualizado"); setEditItem(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const headers = {};
      if (currentEmpresaId) headers["X-Empresa-Id"] = currentEmpresaId;
      const r = await fetch(`${API}/bairros/import-csv`, { method: "POST", credentials: "include", headers, body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Erro");
      toast.success(`Importados: ${d.created} · Ignorados: ${d.skipped} · Novas regiões: ${d.new_regioes || 0}`);
      load();
    } catch (err) { toast.error(err.message || "Erro na importação"); }
    e.target.value = "";
  };

  const csvCell = (v) => { const s = v == null ? "" : String(v); return /[",;\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const exportCsv = () => {
    if (!items.length) return toast.error("Nada para exportar");
    const header = "nome,região";
    const rows = items.map((it) => {
      const reg = regioes.find((r) => r.id === it.regiao_id)?.nome || "";
      return [csvCell(it.nome), csvCell(reg)].join(",");
    });
    const csv = "\ufeff" + [header, ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `bairros-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${items.length} bairros exportados`);
  };

  const bairroOptions = useMemo(() =>
    bairrosGeo.map((b) => ({ value: b, label: b }))
  , [bairrosGeo]);

  return (
    <div className="space-y-6" data-testid="page-bairros">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-extrabold">Bairros</h1>
        <div className="flex gap-2 flex-wrap">
          {canAlterar && items.length > 0 && (
            <Button variant="outline" onClick={() => setSugerirOpen(true)} className="border-sky-300 text-sky-800 hover:bg-sky-50" data-testid="btn-sugerir-regioes" title="Sugere a região de cada bairro pela posição em relação ao centro da cidade">
              <Compass className="w-4 h-4 mr-2" />Sugerir regiões{semRegiaoCount > 0 ? ` (${semRegiaoCount} sem região)` : ""}
            </Button>
          )}
          <Button variant="outline" onClick={exportCsv} data-testid="btn-export-csv-bairros" title="Baixa CSV para importar em outra empresa">
            <Download className="w-4 h-4 mr-2" />Exportar CSV
          </Button>
          <input ref={fileRef} type="file" accept=".csv" hidden onChange={importCsv} data-testid="input-csv" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="btn-import-csv">
            <Upload className="w-4 h-4 mr-2" />Importar CSV
          </Button>
        </div>
      </div>

      {canDescobrir && <CidadeCard key={currentEmpresaId} onResultado={setDescJob} />}
      {descJob && <DescobertaDialog job={descJob} regioes={regioes} onClose={() => setDescJob(null)} onAplicado={load} />}
      <SugerirRegioesDialog open={sugerirOpen} regioes={regioes} onClose={() => setSugerirOpen(false)} onAplicado={load} />

      <div className="text-xs text-muted-foreground bg-orange-50 border border-orange-200 rounded p-3">
        <b>Cadastro assistido:</b> escolha Estado + Cidade e o app busca automaticamente os bairros oficiais no OpenStreetMap. Se não achar o que procura, digite manualmente.
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5 text-orange-600"/>Novo Bairro</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase">Estado</label>
            <Select value={uf} onValueChange={setUf}>
              <SelectTrigger data-testid="select-uf"><SelectValue placeholder="UF" /></SelectTrigger>
              <SelectContent className="max-h-72">{ufs.map((u) => <SelectItem key={u.sigla} value={u.sigla}>{u.sigla} — {u.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase">Cidade</label>
            <Combobox
              value={cidade}
              onChange={setCidade}
              options={cidades.map((c) => ({ value: c, label: c }))}
              placeholder={uf ? "Escolha a cidade" : "Selecione UF primeiro"}
              searchPlaceholder="Buscar cidade..."
              disabled={!uf}
              data-testid="select-cidade"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase flex items-center gap-1">
              Bairro {loadingGeo && <Loader2 className="w-3 h-3 animate-spin"/>}
              {geoSource === "overpass" && bairrosGeo.length > 0 && <span className="text-emerald-600 normal-case">✓ {bairrosGeo.length} da OSM</span>}
              {geoSource === "cache" && <span className="text-sky-600 normal-case">(cache local)</span>}
              {geoSource === "error" && <span className="text-amber-600 normal-case">(digite manualmente)</span>}
              {geoSource === "overpass" && bairrosGeo.length === 0 && <span className="text-amber-600 normal-case">(nenhum encontrado — digite manualmente)</span>}
            </label>
            {bairrosGeo.length > 0 ? (
              <Combobox
                value={bairro}
                onChange={onBairroChange}
                options={bairroOptions}
                placeholder="Escolha ou digite"
                searchPlaceholder="Buscar bairro..."
                allowFreeText
                data-testid="select-bairro"
              />
            ) : (
              <Input value={bairro} onChange={(e) => onBairroChange(e.target.value)}
                placeholder={cidade ? "Digite o nome do bairro" : "Selecione cidade primeiro"}
                disabled={!cidade && bairrosGeo.length === 0} data-testid="input-bairro" />
            )}
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase">Região <span className="text-rose-600" aria-hidden>*</span></label>
            <Select value={regiaoId} onValueChange={onRegiaoChange}>
              <SelectTrigger data-testid="select-regiao" className={!regiaoId ? "border-rose-300" : sugeridaId && regiaoId === sugeridaId && !regiaoManual ? "border-sky-300" : ""}><SelectValue placeholder="Selecione…" /></SelectTrigger>
              <SelectContent>{regioes.map((r) => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}</SelectContent>
            </Select>
            <RegiaoSugestaoHint sugestao={sugestao} aplicada={!!sugeridaId && regiaoId === sugeridaId && !regiaoManual} />
          </div>
          <div className="md:col-span-4">
            <Button onClick={submit} disabled={!bairro || !regiaoId} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-add-bairros">
              <Plus className="w-4 h-4 mr-2" />Adicionar Bairro
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row justify-between items-center gap-3 flex-wrap">
          <CardTitle>Cadastros ({filteredItems.length}{search ? ` de ${items.length}` : ""})</CardTitle>
          <div className="relative"><Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"/>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar" className="pl-8 w-48 h-9" />
          </div>
        </CardHeader>
        <CardContent>
          <Table><TableHeader><TableRow>
            <TableHead className="w-10">
              <input type="checkbox" checked={selected.size > 0 && selected.size === filteredItems.length} onChange={toggleAll}
                className="accent-orange-600 w-4 h-4" data-testid="chk-all-bairros" />
            </TableHead>
            <TableHead>Nome</TableHead><TableHead>Região</TableHead><TableHead className="w-24">Ações</TableHead>
          </TableRow></TableHeader>
            <TableBody>{filteredItems.map((it) => (
              <TableRow key={it.id} className={selected.has(it.id) ? "bg-orange-50/60" : ""}>
                <TableCell><input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSelected(it.id)} className="accent-orange-600 w-4 h-4" data-testid={`chk-${it.id}`} /></TableCell>
                <TableCell className="font-medium">{it.nome}</TableCell>
                <TableCell>{regioes.find((r) => r.id === it.regiao_id)?.nome || "—"}</TableCell>
                <TableCell><div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`btn-edit-${it.id}`}><Pencil className="w-4 h-4 text-orange-600"/></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(it.id)} data-testid={`btn-del-${it.id}`}><Trash2 className="w-4 h-4 text-rose-600"/></Button>
                </div></TableCell>
              </TableRow>))}
              {filteredItems.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Sem registros</TableCell></TableRow>}
            </TableBody></Table>
        </CardContent>
      </Card>

      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar Bairro</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={editForm.nome || ""} onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })} placeholder="Nome" />
            <Select value={editForm.regiao_id || ""} onValueChange={(v) => setEditForm({ ...editForm, regiao_id: v })}>
              <SelectTrigger data-testid="edit-bairro-regiao" className={!editForm.regiao_id ? "border-rose-300" : ""}><SelectValue placeholder="Região (obrigatória)" /></SelectTrigger>
              <SelectContent>{regioes.map((r) => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}</SelectContent>
            </Select>
            {editItem && !editItem.regiao_id && <RegiaoSugestaoHint sugestao={sugestaoEdit} aplicada={!!sugeridaEditId && editForm.regiao_id === sugeridaEditId} testId="edit-regiao-sugestao-hint" />}
            <div className="flex gap-2">
              <Button variant="ghost" className="flex-1" onClick={() => setEditItem(null)} data-testid="edit-bairro-cancelar">Cancelar</Button>
              <Button className="flex-1 bg-orange-600 hover:bg-orange-700" onClick={saveEdit} data-testid="edit-bairro-salvar">Salvar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PasswordConfirm open={!!pwdConfirm} onClose={() => setPwdConfirm(null)} onConfirmed={() => { pwdConfirm?.action?.(); setPwdConfirm(null); }} message={pwdConfirm?.message} resource={pwdConfirm?.resource} action={pwdConfirm?.verifyAction || "e"} />

      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white shadow-2xl border-2 border-rose-300 rounded-full px-4 py-2 flex items-center gap-3" data-testid="bulk-bar">
          <span className="text-sm font-semibold text-rose-700"><b>{selected.size}</b> selecionado(s)</span>
          <Button size="sm" variant="ghost" onClick={clearSelection} className="text-xs h-7" data-testid="btn-bulk-clear">Cancelar</Button>
          <Button size="sm" className="bg-rose-600 hover:bg-rose-700 h-8"
            onClick={() => setPwdConfirm({ action: bulkDelete, message: `Digite as credenciais de um usuário autorizado a excluir ${selected.size} bairro(s):`, resource: "bairros", verifyAction: "e" })}
            data-testid="btn-bulk-delete">
            <Trash2 className="w-4 h-4 mr-1" />Excluir Selecionados
          </Button>
        </div>
      )}
    </div>
  );
}
